import styled, { css } from 'styled-components';
import { usePutGeoSurveyCodesMutation } from '@shared/api';
import {
  codeRuleSourceLabel,
  parsePointRole,
  SURVEY_ROLES,
  type GeoCodeRule,
  type GeoCodeRuleInput,
} from '@entities/geodesy';
import { Button } from '@shared/ui/Button';
import {
  AiBadge,
  CellSelect,
  ErrorText,
  Hint,
  Muted,
  Row,
  SourceTag,
  Stack,
  useAction,
  type Draft,
} from '@widgets/GeoSchemes';
import { useWizard } from './wizard';

const Sub = styled.h4`
  margin: 0;
  font-size: 13px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

/* Своя ширина-контейнер: таблица живёт и в узкой колонке шага 6, и на телефоне. */
const Wrap = styled.div`
  container: geo-codes / inline-size;
`;

/* Без overflow — подсказку бейджа «ИИ-помощник» не обрежет. */
const Table = styled.div`
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.md};
  background: ${({ theme }) => theme.colors.backgroundElevated};
  font-size: 13px;
`;

const columns = css`
  display: grid;
  grid-template-columns: minmax(64px, 1fr) 64px minmax(168px, 2.4fr) 132px;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(3)};
  padding: ${({ theme }) => `${theme.spacing(2)} ${theme.spacing(3)}`};
`;

const HeadRow = styled.div`
  ${columns}
  border-radius: ${({ theme }) => `${theme.radius.md} ${theme.radius.md} 0 0`};
  background: ${({ theme }) => theme.colors.surface};
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.text.secondary};

  .num {
    text-align: right;
  }

  /* Узко: заголовки только для скринридера, подписи — в самих строках. */
  @container geo-codes (max-width: 500px) {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    overflow: hidden;
    clip: rect(0 0 0 0);
    white-space: nowrap;
  }
`;

const CodeRow = styled.div<{ $changed: boolean }>`
  ${columns}
  border-top: 1px solid ${({ theme }) => theme.colors.border};
  ${({ theme, $changed }) =>
    $changed &&
    css`
      box-shadow: inset 3px 0 0 ${theme.colors.status.warning};
    `}

  .code {
    min-width: 0;
    font-family: ${({ theme }) => theme.font.mono};
    font-weight: 700;
    overflow-wrap: anywhere;
  }
  .code.empty {
    font-family: inherit;
    font-weight: 400;
    color: ${({ theme }) => theme.colors.text.secondary};
  }
  .count {
    text-align: right;
    font-family: ${({ theme }) => theme.font.mono};
    font-variant-numeric: tabular-nums;
    white-space: nowrap;
  }
  .count .unit {
    display: none;
  }
  .role select {
    width: 100%;
    min-width: 0;
  }

  @container geo-codes (max-width: 500px) {
    grid-template-columns: minmax(0, 1fr) auto;
    grid-template-areas:
      'code count'
      'role role'
      'source source';
    row-gap: ${({ theme }) => theme.spacing(2)};

    .code {
      grid-area: code;
    }
    .count {
      grid-area: count;
    }
    .count .unit {
      display: inline;
      font-family: ${({ theme }) => theme.font.family};
      color: ${({ theme }) => theme.colors.text.secondary};
    }
    .role {
      grid-area: role;
    }
    .source {
      grid-area: source;
    }
  }
`;

/** Источник роли: ИИ — единый бейдж с подсказкой; «по умолчанию» — приглушённо. */
function CodeSource({ source }: { source: GeoCodeRule['source'] }) {
  if (source === 'llm') return <AiBadge />;
  return <SourceTag $muted={source === 'default'}>{codeRuleSourceLabel(source)}</SourceTag>;
}

/** Правила сервера → строки черновика (код и роль). */
export function codeRuleInputs(rules: GeoCodeRule[]): GeoCodeRuleInput[] {
  return rules.map((r) => ({ code: r.code, role: r.role }));
}

/**
 * «Коды точек» съёмки: сколько точек с каждым кодом, какую роль им дала
 * программа (словарь), ИИ-помощник или человек, — и правка ролей. Сохранение
 * переназначает роли точек и пересчитывает отчёт отклонений.
 */
export function SurveyCodes({
  rules,
  codes,
  pointsDirty,
}: {
  rules: GeoCodeRule[];
  codes: Draft<GeoCodeRuleInput[]>;
  /** Несохранённые правки точек: коды их перезапишут — сначала сохранить. */
  pointsDirty: boolean;
}) {
  const { scheme, readOnly } = useWizard();
  const [put] = usePutGeoSurveyCodesMutation();
  const saving = useAction();

  if (rules.length === 0) return null;

  const draftRole = new Map(codes.draft.map((r) => [r.code, r.role]));

  function setRole(code: string, value: string) {
    const role = parsePointRole(value);
    if (!role) return;
    codes.update((prev) => prev.map((r) => (r.code === code ? { ...r, role } : r)));
  }

  // Без reset() после успеха: черновик совпадёт со свежей карточкой и станет
  // «чистым» сам — не важно, когда RTK сообщит о записи в кеш.
  async function save() {
    await saving.run(
      () => put({ id: scheme.id, rules: codes.draft }).unwrap(),
      'Коды точек не сохранены.',
    );
  }

  return (
    <Stack $gap={3}>
      <Sub id="geo-codes-title">Коды точек</Sub>
      <Hint>
        Роль точки берётся по её коду из файла. «Точка элемента» — верх и низ для вертикальности
        определяются по имени точки (12в / 12н). Реперы и станции отметьте «Не элемент» — в сличение
        они не пойдут.
      </Hint>
      <Wrap>
        <Table role="table" aria-labelledby="geo-codes-title">
          <HeadRow role="row">
            <span role="columnheader">Код</span>
            <span role="columnheader" className="num">
              Точек
            </span>
            <span role="columnheader">Роль</span>
            <span role="columnheader">Источник</span>
          </HeadRow>
          {rules.map((r) => {
            const role = draftRole.get(r.code) ?? r.role;
            const codeName = r.code.trim() === '' ? 'без кода' : r.code;
            return (
              <CodeRow key={r.code} role="row" $changed={role !== r.role}>
                <span role="cell" className={r.code.trim() === '' ? 'code empty' : 'code'}>
                  {codeName}
                </span>
                <span role="cell" className="count">
                  {r.count}
                  <span className="unit"> точ.</span>
                </span>
                <span role="cell" className="role">
                  {readOnly ? (
                    (SURVEY_ROLES.find((x) => x.value === role)?.codeLabel ?? role)
                  ) : (
                    <CellSelect
                      value={role}
                      aria-label={`Роль точек с кодом ${codeName}`}
                      onChange={(e) => setRole(r.code, e.target.value)}
                      disabled={saving.busy}
                    >
                      {SURVEY_ROLES.map((x) => (
                        <option key={x.value} value={x.value}>
                          {x.codeLabel}
                        </option>
                      ))}
                    </CellSelect>
                  )}
                </span>
                <span role="cell" className="source">
                  <CodeSource source={r.source} />
                </span>
              </CodeRow>
            );
          })}
        </Table>
      </Wrap>
      {!readOnly && codes.dirty && (
        <Row $gap={2}>
          <Button
            type="button"
            $variant="primary"
            $size="sm"
            onClick={() => void save()}
            disabled={saving.busy || pointsDirty}
          >
            {saving.busy ? 'Сохранение…' : 'Сохранить коды'}
          </Button>
          <Button
            type="button"
            $variant="ghost"
            $size="sm"
            onClick={codes.reset}
            disabled={saving.busy}
          >
            Отменить
          </Button>
          {pointsDirty && (
            <Muted>Сначала сохраните или отмените правки точек — коды переназначат их роли.</Muted>
          )}
        </Row>
      )}
      {saving.error && <ErrorText role="alert">{saving.error}</ErrorText>}
    </Stack>
  );
}
